/**
 * UI Constants
 * Centralizes all UI-related timeouts, limits, and thresholds
 */

// Timeout values (in milliseconds)
export const TIMEOUTS = {
  SEARCH_DEBOUNCE_MS: 300,
  CART_SYNC_DEBOUNCE_MS: 800,
  ADD_TO_CART_RESET_DELAY_MS: 3000,
  AUTH_INIT_LOAD_DELAY_MS: 500,
  SCROLL_ANIMATION_DELAY_MS: 100,
  VIBRATION_DURATION_MS: 50,
  TOAST_DURATION: 5000,
  TOAST_DURATION_MS: 3000,
  FILTER_SCROLL_TIMEOUT_MS: 100,
  ADMIN_SEARCH_DEBOUNCE_MS: 400,
  INITIAL_LOADING_TIMEOUT_MS: 500,
  COUNTDOWN_UPDATE_INTERVAL_MS: 1000,
  STORY_PROGRESS_UPDATE_INTERVAL_MS: 50,
  MOCK_API_DELAY_MS: 500,
  BUTTON_SCROLL_DELAY_MS: 100,
} as const;

// UI limits
export const LIMITS = {
  // Pagination
  PRODUCTS_PER_PAGE: 12,
  ORDERS_PAGE_SIZE: 10,
  DEFAULT_PAGE_SIZE: 20,
  ADMIN_ORDERS_LIMIT: 50,
  PARTNER_ORDERS_LIMIT: 50,
  USER_ORDERS_LIMIT: 10,
  
  // Display
  WALLET_HISTORY_DISPLAY_LIMIT: 10,
  SHOPS_FETCH_LIMIT: 100,
  FEATURED_PRODUCTS_LIMIT: 6,
  RELATED_PRODUCTS_LIMIT: 6,
  HOME_SHOPS_DISPLAY_LIMIT: 8,
  HOME_SKELETON_COUNT: 6,
  RECOMMENDED_PRODUCTS: 4,
  
  // Stock
  LOW_STOCK_WARN_THRESHOLD: 5,
  
  // Search
  SEARCH_RESULTS: 20,
  BULK_QUERY_LIMIT: 200,
  FILTERED_RESULTS_LIMIT: 100,
  
  // Lists
  CART_SUGGESTIONS: 4,
  REVIEW_TRUNCATE: 150,
  REVIEW_IMAGES_MAX: 3,
  MAX_PRODUCT_IMAGES: 4,
  GUEST_ORDERS_PREVIEW: 2,
  CATEGORY_PREVIEW: 5,
  RELATED_PRODUCTS: 6,
  
  // Firestore
  FIRESTORE_IN_QUERY_LIMIT: 10,
  BATCH_FETCH_SIZE: 10,
} as const;

// UI thresholds
export const THRESHOLDS = {
  IMPULSE_PRICE_THRESHOLD: 20000,
  FREE_SHIPPING_THRESHOLD: 20000,
  TRANSFER_DISCOUNT_THRESHOLD: 40000,
  FREE_SHIPPING_DISPLAY_THRESHOLD: 20000,
  FAST_SHIPPING_SALES_THRESHOLD: 50,
  TOP_RATED_RATING_THRESHOLD: 4.5,
} as const;

// Animation durations (in ms)
export const ANIMATION = {
  FAST: 150,
  NORMAL: 300,
  SLOW: 500,
  EXTRA_SLOW: 800,
} as const;

// Scroll configuration
export const SCROLL = {
  SCROLL_AMOUNT: 150,
  BUTTON_SCROLL_DELAY_MS: 100,
  LOAD_MORE_ORDERS_MIN_THRESHOLD: 10,
  SCROLL_THRESHOLD_PX: 5,
  SCROLL_AMOUNT_PX: 200,
} as const;

// Aspect ratios
export const ASPECT_RATIOS = {
  CREDIT_CARD: '1.58/1',
  ADDRESS: '2/1',
  SHOP_BANNER: '16/9',
  VIDEO: '16/9',
} as const;

// Legacy exports for backward compatibility
export const UI_TIMEOUTS = TIMEOUTS;
export const UI_LIMITS = LIMITS;
export const UI_THRESHOLDS = THRESHOLDS;
export const UI_ANIMATION = ANIMATION;
export const UI_SCROLL = SCROLL;
export const UI_ASPECT_RATIOS = ASPECT_RATIOS;

// Display limits for UI components
export const DISPLAY_LIMITS = {
  SEARCH_RESULTS: 20,
  RECENT_SEARCHES: 10,
  FAVORITES: 100,
  NOTIFICATIONS: 20,
  PRODUCT_IMAGES: 4,
  REVIEW_IMAGES: 3,
  CART_SUGGESTIONS: 4,
  RECOMMENDED_PRODUCTS: 4,
  ORDERS_LOADING: 3,
  ORDER_ITEMS_GRID_THRESHOLD: 3,
  CATALOG_LOADING: 6,
  HOME_RECENT_PRODUCTS: 8,
  HOME_PRODUCTS_PREVIEW: 4,
  FEATURED_SHOPS: 6,
  WALLET_HISTORY_LIMIT: 10,
  RECENT_TRANSACTIONS: 10,
  STORY_SEARCH_RESULTS: 10,
  RELATED_CATEGORY: 6,
  IMPULSE_PRODUCTS_COUNT: 4,
  // Aliases for backwards compatibility
  RELATED_PRODUCTS: 6,
  SHOP_PRODUCTS_PREVIEW: 4,
} as const;

// Combined UI constants
export const UI = {
  TIMEOUTS,
  LIMITS,
  THRESHOLDS,
  ANIMATION,
  SCROLL,
  ASPECT_RATIOS,
  DISPLAY_LIMITS,
} as const;
